/**
 * Unified File Requirements panel with inline upload feedback.
 *
 * Replaces both the old FileRequirementsPanel and PendingFilesList.
 * Shows required/optional vendor file statuses, freshness info, and
 * real-time upload progress on matching rows.
 *
 * Collapsible with smart auto-expand/collapse:
 *  - Expanded when any required file is missing or stale
 *  - Collapsed when all required files are fresh
 *  - Re-expands if a file transitions fresh -> stale
 *  - Collapses after a successful run completes
 *  - Collapses immediately when the user clicks Upload; after the batch finishes,
 *    the panel re-opens automatically if requirements still aren't met, or stays
 *    closed if everything's ready
 *  - Manual toggle overrides auto-rules until fresh->stale or page reload
 */
import React, { useCallback, useEffect, useRef, useState } from "react"
import {
  CheckCircle,
  ChevronDown,
  ChevronRight,
  FolderUp,
  Loader2,
  X,
  XCircle,
} from "lucide-react"

import { PhotoListFileStatus, uploadVendorFile } from "../../api/photoList"
import { THEME } from "../../config/theme"
import { useDealerGroup } from "../../context/DealerGroupContext"
import { useIsMobile } from "../../hooks/useIsMobile"
import { formatAge } from "../../utils/formatAge"
import { detectVendorFromFilename } from "../../utils/vendorDetection"

// ---------------------------------------------------------------------------
// Public types (consumed by DailyPhotoListPage)
// ---------------------------------------------------------------------------

export interface UploadingFile {
  vendor: string
  status: "uploading" | "complete" | "error"
  error?: string
}

interface Props {
  fileStatus: PhotoListFileStatus | null
  loading: boolean
  lastRunCompletedAt: number | null
  uploadingFiles: Map<string, UploadingFile>
  // Upload lifecycle callbacks. Each row in the panel is a click/drop target —
  // selecting a file emits onUploadStart, uploads via /photo-list/upload with
  // the row's vendor (and rooftop, for Stargard rows), then emits complete/error.
  onUploadStart: (files: File[]) => void
  onUploadComplete: (filename: string) => void
  onUploadError: (filename: string, error: string) => void
  uploadDisabled?: boolean
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const EXPECTED_STARGARD_ROOFTOPS = [
  "Toyota",
  "Lexus",
  "Mazda/Kia",
  "Dodge",
  "Fernley CDJR",
  "Mill/Driven",
]

// Must match backend REQUIRED_STARGARD_ROOFTOPS verbatim (slash and space included).
const STARGARD_ROOFTOPS = [
  "Toyota",
  "Lexus",
  "Mazda/Kia",
  "Dodge",
  "Fernley CDJR",
  "Mill/Driven",
] as const

// Stock prefix → rooftop heuristic. Must mirror STOCK_PREFIX_ROOFTOP_MAP in
// backend/services/photo_list_validation.py — keep in sync. Multi-character
// prefixes are checked first (longest match wins) so DMK beats M, K, D.
const STOCK_PREFIX_ROOFTOP_MAP: Record<string, string> = {
  DMK: "Mazda/Kia",
  RR: "Dodge",
  RD: "Dodge",
  T: "Toyota",
  L: "Lexus",
  M: "Mazda/Kia",
  K: "Mazda/Kia",
  F: "Fernley CDJR",
  C: "Fernley CDJR",
  J: "Fernley CDJR",
  D: "Dodge",
}
const STOCK_PREFIX_ORDER = Object.keys(STOCK_PREFIX_ROOFTOP_MAP).sort(
  (a, b) => b.length - a.length,
)

const ROOFTOP_DOMINANCE_THRESHOLD = 0.6

// Read enough of the file to get a representative sample of rows. 100KB is
// well over 100 rows for a Stargard CSV — plenty for the prefix dominance test.
const ROOFTOP_DETECTION_READ_BYTES = 100_000

const MAX_FILENAME_LEN = 60

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Returns true when any required file is missing or stale. */
function hasUnreadyFiles(fileStatus: PhotoListFileStatus): boolean {
  return !fileStatus.all_required_present || !fileStatus.all_required_fresh
}

type IconType = "check" | "missing" | "stale" | "optional" | "uploading" | "error"

function getIcon(
  status: "present" | "missing" | "stale",
  required: boolean,
): IconType {
  if (status === "present") return "check"
  if (status === "stale") return "stale"
  if (!required) return "optional"
  return "missing"
}

function truncFilename(name: string): string {
  if (name.length <= MAX_FILENAME_LEN) return name
  return name.slice(0, MAX_FILENAME_LEN) + "..."
}

function getDetail(entry: {
  status: string
  filename?: string
  age_seconds?: number
  fresh?: boolean
  required: boolean
}): { text: string; title?: string } {
  if (entry.status === "missing") {
    return { text: "not uploaded" }
  }
  const fullName = entry.filename ?? ""
  const name = truncFilename(fullName)
  const title = fullName.length > MAX_FILENAME_LEN ? fullName : undefined
  return { text: name, title }
}

/** Count files actively uploading for a given vendor key. */
function countByVendorStatus(
  uploadingFiles: Map<string, UploadingFile>,
  vendor: string,
  status: UploadingFile["status"],
): number {
  let count = 0
  Array.from(uploadingFiles.values()).forEach((uf) => {
    if (uf.vendor === vendor && uf.status === status) count++
  })
  return count
}

/** Get first error for a vendor. */
function getVendorError(
  uploadingFiles: Map<string, UploadingFile>,
  vendor: string,
): string | undefined {
  const entries = Array.from(uploadingFiles.values())
  for (let i = 0; i < entries.length; i++) {
    if (entries[i].vendor === vendor && entries[i].status === "error") return entries[i].error
  }
  return undefined
}

/** Compute upload overlay for a vendor row. */
function getUploadOverlay(
  uploadingFiles: Map<string, UploadingFile>,
  vendor: string,
): { icon: IconType; suffix: string; error?: string } | null {
  const uploading = countByVendorStatus(uploadingFiles, vendor, "uploading")
  if (uploading > 0) {
    return { icon: "uploading", suffix: "uploading..." }
  }
  const errored = countByVendorStatus(uploadingFiles, vendor, "error")
  if (errored > 0) {
    const msg = getVendorError(uploadingFiles, vendor)
    return { icon: "error", suffix: msg ?? "upload failed", error: msg }
  }
  const complete = countByVendorStatus(uploadingFiles, vendor, "complete")
  if (complete > 0) {
    return { icon: "check", suffix: "just now" }
  }
  return null
}

// ---------------------------------------------------------------------------
// Header summary helpers
// ---------------------------------------------------------------------------

function buildSummary(
  fileStatus: PhotoListFileStatus | null,
  uploadingFiles: Map<string, UploadingFile>,
): { text: string; color: string } {
  // Active uploads take priority
  let totalUploading = 0
  let totalComplete = 0
  let totalFiles = 0
  Array.from(uploadingFiles.values()).forEach((uf) => {
    totalFiles++
    if (uf.status === "uploading") totalUploading++
    if (uf.status === "complete") totalComplete++
  })
  if (totalUploading > 0) {
    return {
      text: `uploading... (${totalComplete + totalUploading - totalUploading} of ${totalFiles})`,
      color: THEME.colors.navy,
    }
  }

  if (!fileStatus) return { text: "", color: THEME.colors.neutral.light.textSecondary }

  if (!hasUnreadyFiles(fileStatus)) {
    return { text: "all ready", color: THEME.colors.semantic.success }
  }

  // Count missing + stale
  let missingCount = 0
  let staleCount = 0
  const { files } = fileStatus
  for (const key of Object.keys(files)) {
    if (key === "stargard") continue // handled separately
    const f = files[key]
    if (f.required) {
      if (f.status === "missing") missingCount++
      if (f.status === "stale") staleCount++
    }
  }
  // Stargard missing rooftops
  missingCount += fileStatus.stargard_missing.length
  if (files.stargard?.stale_count) staleCount += files.stargard.stale_count

  const issues = missingCount + staleCount
  if (missingCount > 0 && staleCount === 0) {
    return {
      text: `${missingCount} required file${missingCount > 1 ? "s" : ""} missing`,
      color: THEME.colors.semantic.warning,
    }
  }
  if (issues > 0) {
    return {
      text: `${issues} issue${issues > 1 ? "s" : ""}`,
      color: THEME.colors.semantic.warning,
    }
  }
  return { text: "all ready", color: THEME.colors.semantic.success }
}

// ---------------------------------------------------------------------------
// Progress bar
// ---------------------------------------------------------------------------

function UploadProgressBar({
  uploadingFiles,
}: {
  uploadingFiles: Map<string, UploadingFile>
}) {
  let total = 0
  let done = 0
  Array.from(uploadingFiles.values()).forEach((uf) => {
    total++
    if (uf.status === "complete" || uf.status === "error") done++
  })
  if (total === 0) return null

  const anyUploading = done < total
  if (!anyUploading) return null

  const pct = total > 0 ? (done / total) * 100 : 0

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 6,
        marginLeft: "auto",
        fontSize: 11,
        color: THEME.colors.navy,
        fontWeight: 500,
      }}
    >
      <span>
        {done} of {total}
      </span>
      <div
        style={{
          width: 48,
          height: 4,
          borderRadius: 2,
          backgroundColor: THEME.colors.silver,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            width: `${pct}%`,
            height: "100%",
            backgroundColor: THEME.colors.navy,
            borderRadius: 2,
            transition: "width 200ms ease",
          }}
        />
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Staging types + components — pending uploads live inside the panel
// ---------------------------------------------------------------------------

type PendingStatus =
  | { kind: "pending" }
  | { kind: "uploading" }
  | { kind: "success" }
  | { kind: "error"; message: string }

interface PendingFile {
  id: string
  file: File
  vendor: string
  // Empty string = "Auto-detect rooftop" (server uses stock-prefix heuristic).
  // Only meaningful when vendor === "stargard".
  stargardRooftop: string
  status: PendingStatus
}

function StagingDropZone({
  onAddFiles,
  disabled,
}: {
  onAddFiles: (files: File[]) => void
  disabled?: boolean
}) {
  const [isDragOver, setIsDragOver] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  // Touch devices can't drag files — the tap already opens the file picker,
  // so the label just has to say so.
  const isMobile = useIsMobile()

  const handleClick = () => {
    if (disabled) return
    inputRef.current?.click()
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      onAddFiles(Array.from(e.target.files))
    }
    if (inputRef.current) inputRef.current.value = ""
  }

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)
    if (disabled) return
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      onAddFiles(Array.from(e.dataTransfer.files))
    }
  }

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    if (!disabled) setIsDragOver(true)
  }

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)
  }

  return (
    <div
      onClick={handleClick}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragEnter={handleDragOver}
      onDragLeave={handleDragLeave}
      style={{
        border: `1.5px dashed ${
          isDragOver ? THEME.colors.navy : THEME.colors.silver
        }`,
        borderRadius: THEME.borderRadius.md,
        padding: "13px 12px",
        background: isDragOver
          ? "rgba(13, 26, 66, 0.06)"
          : THEME.colors.neutral.light.background,
        textAlign: "center",
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.6 : 1,
        transition: "background 120ms ease, border-color 120ms ease",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        fontSize: 12,
        color: THEME.colors.neutral.light.textSecondary,
      }}
    >
      <FolderUp size={14} strokeWidth={1.75} />
      {isDragOver
        ? "Drop files here"
        : isMobile
          ? "Tap to choose files"
          : "Drag files here or click to browse"}
      <input
        ref={inputRef}
        type="file"
        multiple
        onChange={handleFileChange}
        style={{ display: "none" }}
      />
    </div>
  )
}

function PendingRow({
  entry,
  onRooftopChange,
  onRemove,
}: {
  entry: PendingFile
  onRooftopChange: (id: string, rooftop: string) => void
  onRemove: (id: string) => void
}) {
  const isUnknown = entry.vendor === "unknown"
  const rowDisabled =
    entry.status.kind === "uploading" || entry.status.kind === "success"

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "6px 4px",
        fontSize: 13,
        borderBottom: `1px solid ${THEME.colors.silver}`,
      }}
    >
      <span
        title={entry.file.name}
        style={{
          flex: 1,
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          color: THEME.colors.neutral.light.textPrimary,
          fontWeight: 500,
        }}
      >
        {entry.file.name}
      </span>
      <span
        style={{
          fontSize: 12,
          color: isUnknown
            ? THEME.colors.semantic.warning
            : THEME.colors.neutral.light.textSecondary,
          fontStyle: isUnknown ? "italic" : "normal",
          minWidth: 80,
        }}
      >
        {isUnknown ? "Unknown" : labelForVendor(entry.vendor)}
      </span>
      {entry.vendor === "stargard" && (
        <select
          value={entry.stargardRooftop}
          onChange={(e) => onRooftopChange(entry.id, e.target.value)}
          disabled={rowDisabled}
          title="Pre-selected by stock-prefix heuristic. Override here if auto-detect picked the wrong rooftop."
          style={{
            fontSize: 12,
            padding: "2px 6px",
            border: `1px solid ${
              entry.stargardRooftop
                ? THEME.colors.silver
                : THEME.colors.semantic.warning
            }`,
            borderRadius: THEME.borderRadius.md,
            fontFamily: THEME.fonts.primary,
            color: THEME.colors.neutral.light.textPrimary,
            background: THEME.colors.neutral.light.surface,
          }}
        >
          <option value="" disabled hidden>
            Select Rooftop
          </option>
          {STARGARD_ROOFTOPS.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
      )}
      {renderPendingStatus(entry.status)}
      <button
        type="button"
        onClick={() => onRemove(entry.id)}
        disabled={entry.status.kind === "uploading"}
        title="Remove"
        style={{
          display: "inline-flex",
          alignItems: "center",
          background: "none",
          border: "none",
          color: THEME.colors.neutral.light.textSecondary,
          cursor:
            entry.status.kind === "uploading" ? "not-allowed" : "pointer",
          padding: "0 4px",
        }}
      >
        <X size={14} strokeWidth={2} />
      </button>
    </div>
  )
}

function renderPendingStatus(status: PendingStatus): React.ReactNode {
  switch (status.kind) {
    case "uploading":
      return (
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            fontSize: 12,
            color: THEME.colors.navy,
          }}
        >
          <Loader2 size={12} strokeWidth={2} className="invi-spin" />
          Uploading...
        </span>
      )
    case "success":
      return (
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            fontSize: 12,
            color: THEME.colors.semantic.success,
          }}
        >
          <CheckCircle size={12} strokeWidth={2} />
          Uploaded
        </span>
      )
    case "error":
      return (
        <span
          title={status.message}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            fontSize: 12,
            color: THEME.colors.semantic.danger,
          }}
        >
          <XCircle size={12} strokeWidth={2} />
          {status.message}
        </span>
      )
    default:
      return null
  }
}

const VENDOR_LABEL_MAP: Record<string, string> = {
  tekion: "Tekion",
  rapidrecon: "Rapid Recon",
  homenet: "Homenet",
  stargard: "Stargard",
  dealerconnect: "DealerConnect",
  dealerdaily_toyota: "Dealer Daily Toyota",
  autoexact: "AutoExact",
}

function labelForVendor(vendor: string): string {
  return VENDOR_LABEL_MAP[vendor] ?? vendor
}

// ---------------------------------------------------------------------------
// Client-side Stargard rooftop detection — mirrors the backend heuristic so the
// rooftop dropdown can pre-select an accurate value as soon as a file lands in
// the pending list. Returns null if the file can't be classified (no Stock #
// column, no recognizable prefixes, read error) — caller surfaces "Select Rooftop"
// in that case so the user picks manually.
// ---------------------------------------------------------------------------

function parseCsvLine(line: string): string[] {
  const cells: string[] = []
  let current = ""
  let inQuotes = false
  for (let i = 0; i < line.length; i++) {
    const ch = line[i]
    if (inQuotes) {
      if (ch === '"' && line[i + 1] === '"') {
        current += '"'
        i++
      } else if (ch === '"') {
        inQuotes = false
      } else {
        current += ch
      }
    } else if (ch === ",") {
      cells.push(current)
      current = ""
    } else if (ch === '"') {
      inQuotes = true
    } else {
      current += ch
    }
  }
  cells.push(current)
  return cells
}

function cleanStock(raw: string): string {
  let s = raw.trim()
  if (s.startsWith('="') && s.endsWith('"')) s = s.slice(2, -1)
  else if (s.startsWith("='") && s.endsWith("'")) s = s.slice(2, -1)
  return s.trim().toUpperCase()
}

function stockPrefixToRooftop(stock: string): string | null {
  for (const prefix of STOCK_PREFIX_ORDER) {
    if (stock.startsWith(prefix)) return STOCK_PREFIX_ROOFTOP_MAP[prefix]
  }
  return null
}

async function detectRooftopFromCsv(file: File): Promise<string | null> {
  try {
    const slice = file.slice(0, ROOFTOP_DETECTION_READ_BYTES)
    const text = await slice.text()
    const lines = text.split(/\r?\n/)
    if (lines.length < 2) return null
    // Strip BOM from header line if present.
    const headerLine = lines[0].replace(/^﻿/, "")
    const headers = parseCsvLine(headerLine)
    const stockIdx = headers.findIndex((h) => h.trim() === "Stock #")
    if (stockIdx < 0) return null

    const counts = new Map<string, number>()
    let total = 0
    for (let i = 1; i < lines.length; i++) {
      if (!lines[i]) continue
      const cells = parseCsvLine(lines[i])
      if (cells.length <= stockIdx) continue
      const stock = cleanStock(cells[stockIdx])
      if (!stock) continue
      const rooftop = stockPrefixToRooftop(stock)
      if (rooftop) {
        counts.set(rooftop, (counts.get(rooftop) ?? 0) + 1)
        total++
      }
    }
    if (total === 0) return null

    let topRooftop = ""
    let topCount = 0
    counts.forEach((count, rooftop) => {
      if (count > topCount) {
        topCount = count
        topRooftop = rooftop
      }
    })
    if (topCount / total >= ROOFTOP_DOMINANCE_THRESHOLD) return topRooftop
    // Mixed prefixes — backend treats as Mill/Driven (shared lot).
    return "Mill/Driven"
  } catch {
    return null
  }
}

// ---------------------------------------------------------------------------
// FileRow
// ---------------------------------------------------------------------------

const ICON_STYLES: Record<IconType, { symbol: string; color: string }> = {
  check: { symbol: "✓", color: THEME.colors.semantic.success },
  missing: { symbol: "○", color: THEME.colors.neutral.light.textSecondary },
  stale: { symbol: "▲", color: THEME.colors.semantic.warning },
  optional: { symbol: "○", color: THEME.colors.neutral.light.textSecondary },
  uploading: { symbol: "↻", color: THEME.colors.navy },
  error: { symbol: "✗", color: THEME.colors.semantic.danger },
}

function FileRow({
  icon,
  label,
  text,
  title,
  ageText,
  errorText,
}: {
  icon: IconType
  label: string
  text: string
  title?: string
  ageText?: string
  errorText?: string
}) {
  const { symbol, color } = ICON_STYLES[icon]
  const isUploading = icon === "uploading"
  // On a phone the fixed 155px label plus filename plus age badge can't share
  // one row — the filename drops to its own indented second line instead.
  const isMobile = useIsMobile()

  const statusTextStyle: React.CSSProperties = {
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    color:
      icon === "missing" || icon === "stale" || icon === "error"
        ? color
        : THEME.colors.neutral.light.textSecondary,
    fontStyle: icon === "missing" || icon === "optional" ? "italic" : "normal",
  }

  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: THEME.spacing.sm,
          padding: "3px 0",
          fontSize: 13,
          minHeight: 22,
        }}
      >
        {/* Status icon */}
        <span
          className={isUploading ? "invi-spin" : undefined}
          style={{
            width: 18,
            textAlign: "center",
            flexShrink: 0,
            color,
            fontWeight: 600,
            fontSize: 14,
          }}
        >
          {symbol}
        </span>

        {/* Label */}
        <span
          style={{
            ...(isMobile ? { flex: 1, minWidth: 0 } : { width: 155, flexShrink: 0 }),
            fontWeight: 500,
            color: THEME.colors.neutral.light.textPrimary,
          }}
        >
          {label}
        </span>

        {/* Filename / status text (desktop: inline; mobile: second line below) */}
        {!isMobile && (
          <span title={title} style={{ ...statusTextStyle, flex: 1 }}>
            {text}
          </span>
        )}

        {/* Age / status badge (right-aligned) */}
        {ageText && (
          <span
            style={{
              flexShrink: 0,
              fontSize: 12,
              color,
              fontWeight: ageText === "STALE" ? 600 : 400,
              whiteSpace: "nowrap",
            }}
          >
            {ageText}
          </span>
        )}
      </div>

      {/* Mobile second line: filename / status text, indented past the icon */}
      {isMobile && text && (
        <div
          title={title}
          style={{ ...statusTextStyle, marginLeft: 26, fontSize: 12, padding: "0 0 2px 0" }}
        >
          {text}
        </div>
      )}

      {/* Error detail line */}
      {errorText && (
        <div
          style={{
            marginLeft: 26,
            fontSize: 12,
            color: THEME.colors.semantic.danger,
            padding: "0 0 2px 0",
          }}
        >
          {errorText}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Build row props — merges file status with upload overlay
// ---------------------------------------------------------------------------

function buildRowProps(
  icon: IconType,
  label: string,
  text: string,
  title: string | undefined,
  ageSeconds: number | undefined,
  fresh: boolean | undefined,
  uploadOverlay: ReturnType<typeof getUploadOverlay>,
): React.ComponentProps<typeof FileRow> {
  // Upload overlay takes precedence for icon and right-side text
  if (uploadOverlay) {
    return {
      icon: uploadOverlay.icon,
      label,
      text: uploadOverlay.icon === "uploading" ? text || "uploading..." : text,
      title,
      ageText: uploadOverlay.suffix,
      errorText: uploadOverlay.icon === "error" ? uploadOverlay.error : undefined,
    }
  }

  // Normal state — derive ageText from age_seconds
  let ageText: string | undefined
  if (icon === "check" && ageSeconds != null) {
    ageText = formatAge(ageSeconds)
  } else if (icon === "stale" && ageSeconds != null) {
    ageText = `${formatAge(ageSeconds)} — STALE`
  }

  return { icon, label, text, title, ageText }
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function FileRequirementsPanel({
  fileStatus,
  loading,
  lastRunCompletedAt,
  uploadingFiles,
  onUploadStart,
  onUploadComplete,
  onUploadError,
  uploadDisabled,
}: Props) {
  // File storage is per dealer group — uploads must target the active group's
  // partition. The page already requires a concrete group to render.
  const { dealerGroupId } = useDealerGroup()
  // ---------------------- Internal staging state ----------------------
  // Files dropped/picked land here before upload. The user reviews each row
  // (and overrides the Stargard rooftop where needed) and then commits with
  // the Upload button.
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([])
  const [isUploadingBatch, setIsUploadingBatch] = useState(false)

  const addPendingFiles = useCallback((files: File[]) => {
    if (files.length === 0) return
    const incoming: PendingFile[] = files.map((f) => ({
      id: `${f.name}-${f.size}-${f.lastModified}-${Math.random()
        .toString(36)
        .slice(2, 8)}`,
      file: f,
      vendor: detectVendorFromFilename(f.name),
      stargardRooftop: "",
      status: { kind: "pending" },
    }))
    setPendingFiles((prev) => [...prev, ...incoming])

    // For Stargard files, run the client-side rooftop heuristic and pre-select
    // it in the dropdown. If detection fails, the dropdown stays empty and the
    // UI shows "Select Rooftop" — user must pick before upload enables.
    for (const entry of incoming) {
      if (entry.vendor !== "stargard") continue
      void detectRooftopFromCsv(entry.file).then((rooftop) => {
        if (!rooftop) return
        setPendingFiles((prev) =>
          prev.map((p) =>
            p.id === entry.id && !p.stargardRooftop
              ? { ...p, stargardRooftop: rooftop }
              : p,
          ),
        )
      })
    }
  }, [])

  const removePending = useCallback((id: string) => {
    setPendingFiles((prev) => prev.filter((p) => p.id !== id))
  }, [])

  const setPendingRooftop = useCallback((id: string, rooftop: string) => {
    setPendingFiles((prev) =>
      prev.map((p) => (p.id === id ? { ...p, stargardRooftop: rooftop } : p)),
    )
  }, [])

  const uploadAllPending = useCallback(async () => {
    const toUpload = pendingFiles.filter((p) => p.status.kind === "pending")
    if (toUpload.length === 0) return
    // Collapse the panel immediately on Upload click — uploads continue in the
    // background while the panel is closed. The fileStatus effect handles
    // re-opening if requirements aren't met after the batch finishes.
    setManualOverride(false)
    setExpanded(false)
    onUploadStart(toUpload.map((p) => p.file))
    setIsUploadingBatch(true)
    for (const entry of toUpload) {
      setPendingFiles((prev) =>
        prev.map((p) =>
          p.id === entry.id ? { ...p, status: { kind: "uploading" } } : p,
        ),
      )
      try {
        await uploadVendorFile(
          entry.file,
          {
            stargardRooftop:
              entry.vendor === "stargard" && entry.stargardRooftop
                ? entry.stargardRooftop
                : undefined,
          },
          dealerGroupId,
        )
        setPendingFiles((prev) =>
          prev.map((p) =>
            p.id === entry.id ? { ...p, status: { kind: "success" } } : p,
          ),
        )
        onUploadComplete(entry.file.name)
      } catch (err) {
        const message = err instanceof Error ? err.message : "Upload failed"
        setPendingFiles((prev) =>
          prev.map((p) =>
            p.id === entry.id
              ? { ...p, status: { kind: "error", message } }
              : p,
          ),
        )
        onUploadError(entry.file.name, message)
      }
    }
    setIsUploadingBatch(false)
    // Pause briefly so the user sees the success ticks, then drop succeeded entries.
    await new Promise((resolve) => setTimeout(resolve, 1500))
    setPendingFiles((prev) => prev.filter((p) => p.status.kind !== "success"))
    // Hand expand/collapse back to the fileStatus effect: if requirements are
    // still unmet, it re-opens the panel; if everything's ready, it stays closed.
    setManualOverride(undefined)
  }, [pendingFiles, dealerGroupId, onUploadStart, onUploadComplete, onUploadError])

  // Expand state: undefined = no manual override, derive from fileStatus
  const [manualOverride, setManualOverride] = useState<boolean | undefined>(undefined)
  const [expanded, setExpanded] = useState(true)
  const prevCanRunRef = useRef<boolean | null>(null)
  const prevRunCompletedRef = useRef<number | null>(null)

  // Derive expanded state from fileStatus + manual override
  useEffect(() => {
    if (!fileStatus) return

    const unready = hasUnreadyFiles(fileStatus)

    // If files went FROM ready TO unready, auto-expand and clear manual override
    if (prevCanRunRef.current === true && unready) {
      setManualOverride(undefined)
      setExpanded(true)
    } else if (manualOverride !== undefined) {
      // Manual override active — respect it
      setExpanded(manualOverride)
    } else {
      // No manual override — derive from file readiness
      setExpanded(unready)
    }

    prevCanRunRef.current = !unready
  }, [fileStatus, manualOverride])

  // Collapse after successful run completion
  useEffect(() => {
    if (
      lastRunCompletedAt &&
      lastRunCompletedAt !== prevRunCompletedRef.current
    ) {
      prevRunCompletedRef.current = lastRunCompletedAt
      setManualOverride(undefined)
      setExpanded(false)
    }
  }, [lastRunCompletedAt])

  // Auto-expand only when files are *newly* added to the staging list — not
  // whenever the count is non-zero. Watching `expanded` here would fight the
  // immediate collapse on Upload click (panel would flicker back open while
  // pending uploads are still in flight). Set manualOverride=true so the
  // readiness-driven fileStatus effect can't auto-collapse the panel while the
  // user is staging files (would happen when existing files already satisfy
  // requirements but the user wants to add more anyway).
  const prevPendingCountRef = useRef(0)
  useEffect(() => {
    if (pendingFiles.length > prevPendingCountRef.current) {
      setExpanded(true)
      setManualOverride(true)
    }
    prevPendingCountRef.current = pendingFiles.length
  }, [pendingFiles.length])

  const handleToggle = () => {
    const next = !expanded
    setManualOverride(next)
    setExpanded(next)
  }

  if (loading && !fileStatus) {
    return (
      <div
        style={{
          border: `1px solid ${THEME.colors.silver}`,
          borderRadius: THEME.borderRadius.md,
          padding: THEME.spacing.md,
          backgroundColor: THEME.colors.neutral.light.surface,
          fontSize: 13,
          color: THEME.colors.neutral.light.textSecondary,
        }}
      >
        Loading file status...
      </div>
    )
  }

  if (!fileStatus) return null

  const { files } = fileStatus
  const summary = buildSummary(fileStatus, uploadingFiles)

  return (
    <div
      style={{
        border: `1px solid ${THEME.colors.silver}`,
        borderRadius: THEME.borderRadius.md,
        backgroundColor: THEME.colors.neutral.light.surface,
        overflow: "hidden",
      }}
    >
      {/* Collapsible header */}
      <div
        onClick={handleToggle}
        style={{
          display: "flex",
          alignItems: "center",
          gap: THEME.spacing.sm,
          padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
          cursor: "pointer",
          userSelect: "none",
          fontSize: 13,
          fontWeight: 600,
          color: THEME.colors.neutral.light.textPrimary,
        }}
      >
        {expanded ? (
          <ChevronDown
            size={14}
            strokeWidth={2}
            style={{ color: THEME.colors.neutral.light.textSecondary }}
          />
        ) : (
          <ChevronRight
            size={14}
            strokeWidth={2}
            style={{ color: THEME.colors.neutral.light.textSecondary }}
          />
        )}
        File Requirements
        <span
          style={{
            fontWeight: 400,
            fontSize: 12,
            marginLeft: 4,
            color: summary.color,
          }}
        >
          — {summary.text}
        </span>
        <UploadProgressBar uploadingFiles={uploadingFiles} />
      </div>

      {expanded && (
        <div style={{ padding: `0 ${THEME.spacing.md} ${THEME.spacing.md}` }}>
          {/* Staging area: drop zone + pending uploads + Upload button */}
          <div style={{ marginBottom: THEME.spacing.sm }}>
            <StagingDropZone
              onAddFiles={addPendingFiles}
              disabled={uploadDisabled || isUploadingBatch}
            />
            {pendingFiles.length > 0 && (() => {
              const pendingCount = pendingFiles.filter(
                (p) => p.status.kind === "pending",
              ).length
              const stargardWithoutRooftop = pendingFiles.some(
                (p) =>
                  p.status.kind === "pending" &&
                  p.vendor === "stargard" &&
                  !p.stargardRooftop,
              )
              const uploadButtonDisabled =
                uploadDisabled ||
                isUploadingBatch ||
                pendingCount === 0 ||
                stargardWithoutRooftop

              return (
                <div style={{ marginTop: THEME.spacing.sm }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      marginBottom: 4,
                    }}
                  >
                    <div
                      style={{
                        fontSize: 12,
                        fontWeight: 500,
                        color: THEME.colors.neutral.light.textSecondary,
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                      }}
                    >
                      Pending Uploads
                    </div>
                    <button
                      type="button"
                      onClick={uploadAllPending}
                      disabled={uploadButtonDisabled}
                      title={
                        stargardWithoutRooftop
                          ? "Select a rooftop for every Stargard file before uploading."
                          : undefined
                      }
                      style={{
                        padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
                        fontSize: 14,
                        fontWeight: 500,
                        borderRadius: THEME.borderRadius.md,
                        border: "none",
                        backgroundColor: THEME.colors.navy,
                        color: THEME.colors.white,
                        opacity: uploadButtonDisabled ? 0.5 : 1,
                        cursor: uploadButtonDisabled ? "not-allowed" : "pointer",
                        fontFamily: THEME.fonts.primary,
                        transition: "opacity 0.15s",
                      }}
                    >
                      {isUploadingBatch
                        ? "Uploading..."
                        : `Upload ${pendingCount} ${
                            pendingCount === 1 ? "File" : "Files"
                          }`}
                    </button>
                  </div>
                  {pendingFiles.map((entry) => (
                    <PendingRow
                      key={entry.id}
                      entry={entry}
                      onRooftopChange={setPendingRooftop}
                      onRemove={removePending}
                    />
                  ))}
                </div>
              )
            })()}
          </div>

          {/* Divider between staging and status sections */}
          <div
            style={{
              borderTop: `1px solid ${THEME.colors.silver}`,
              margin: `${THEME.spacing.sm} 0`,
            }}
          />

          {/* Required section */}
          <div style={{ marginBottom: THEME.spacing.sm }}>
            <div
              style={{
                fontSize: 12,
                fontWeight: 500,
                color: THEME.colors.neutral.light.textSecondary,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                marginBottom: 4,
              }}
            >
              Required
            </div>

            {/* Homenet */}
            {files.homenet && renderSimpleRow(files.homenet, "homenet", uploadingFiles)}

            {/* Stargard — one row per expected rooftop */}
            {EXPECTED_STARGARD_ROOFTOPS.map((rooftop) => {
              const matchingFile = files.stargard?.files?.find(
                (f) => f.rooftop === rooftop,
              )
              const isCovered = fileStatus.stargard_coverage.includes(rooftop)
              const overlay = getUploadOverlay(uploadingFiles, "stargard")

              let rowProps: React.ComponentProps<typeof FileRow>
              if (matchingFile) {
                const isFresh = matchingFile.fresh
                const baseIcon: IconType = isFresh ? "check" : "stale"
                const displayName = truncFilename(matchingFile.filename)
                const fullTitle =
                  matchingFile.filename.length > MAX_FILENAME_LEN
                    ? matchingFile.filename
                    : undefined
                rowProps = buildRowProps(
                  baseIcon,
                  `Stargard: ${rooftop}`,
                  displayName,
                  fullTitle,
                  matchingFile.age_seconds,
                  matchingFile.fresh,
                  overlay,
                )
              } else {
                const missingText = isCovered
                  ? "detected but file info unavailable"
                  : "not uploaded"
                rowProps = buildRowProps(
                  "missing",
                  `Stargard: ${rooftop}`,
                  missingText,
                  undefined,
                  undefined,
                  undefined,
                  overlay,
                )
              }

              return <FileRow key={rooftop} {...rowProps} />
            })}

            {/* AutoExact */}
            {files.autoexact &&
              renderSimpleRow(files.autoexact, "autoexact", uploadingFiles)}
          </div>

          {/* Divider */}
          <div
            style={{
              borderTop: `1px solid ${THEME.colors.silver}`,
              margin: `${THEME.spacing.sm} 0`,
            }}
          />

          {/* Optional section */}
          <div>
            <div
              style={{
                fontSize: 12,
                fontWeight: 500,
                color: THEME.colors.neutral.light.textSecondary,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                marginBottom: 4,
              }}
            >
              Optional
            </div>

            {files.dealer_daily_toyota &&
              renderSimpleRow(files.dealer_daily_toyota, "dealerdaily_toyota", uploadingFiles)}

            {/* Dealer Connect — one row per rooftop (Dodge, Fernley Jeep) */}
            {fileStatus.dealer_connect?.rooftops?.map((rt) => {
              const overlay = getUploadOverlay(uploadingFiles, "dealerconnect")
              const baseIcon: IconType = rt.status === "present"
                ? "check"
                : rt.status === "stale"
                  ? "stale"
                  : "optional"
              const text = rt.filename
                ? truncFilename(rt.filename)
                : "not uploaded"
              const title = rt.filename && rt.filename.length > MAX_FILENAME_LEN
                ? rt.filename
                : undefined

              const rowProps = buildRowProps(
                baseIcon,
                rt.label,
                text,
                title,
                rt.age_seconds ?? undefined,
                rt.fresh,
                overlay,
              )
              return <FileRow key={rt.dealer_code} {...rowProps} />
            })}
          </div>
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Render helper for simple (non-stargard) rows
// ---------------------------------------------------------------------------

function renderSimpleRow(
  entry: {
    label: string
    status: "present" | "missing" | "stale"
    filename?: string
    age_seconds?: number
    fresh?: boolean
    required: boolean
  },
  vendorKey: string,
  uploadingFiles: Map<string, UploadingFile>,
) {
  const baseIcon = getIcon(entry.status, entry.required)
  const detail = getDetail(entry)
  const overlay = getUploadOverlay(uploadingFiles, vendorKey)

  const props = buildRowProps(
    baseIcon,
    entry.label,
    detail.text,
    detail.title,
    entry.age_seconds,
    entry.fresh,
    overlay,
  )
  return <FileRow {...props} />
}
